import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { ToastProvider } from './hooks/useToast';
import Layout          from './components/Layout';
import Login           from './pages/auth/Login';
import Register        from './pages/auth/Register';
import Dashboard       from './pages/Dashboard';
import ChartOfAccounts from './pages/ChartOfAccounts';
import Accounts from './pages/Accounts';
import Contacts        from './pages/Contacts';
import Products        from './pages/Products';
import OpeningBalances from './pages/OpeningBalances';
import Settings from './pages/Settings';
import Journals from './pages/Journals';
import Reports from './pages/Reports';
import Inventory from './pages/Inventory';
import Sales from './pages/Sales';
import ServiceTickets from './pages/ServiceTickets';
import QuickQuote from './pages/QuickQuote';
import PaymentAccounts from './pages/PaymentAccounts';
import OpeningStockLines from './pages/OpeningStockLines';
import ExpiryTracking from './pages/ExpiryTracking';
import Enquiries from './pages/Enquiries';
import FollowUps from './pages/FollowUps';
import StockAdjustment from './pages/StockAdjustment';
import StockTransfer from './pages/StockTransfer';
import PhysicalCount from './pages/PhysicalCount';
import SalesReturns from './pages/SalesReturns';
import PurchaseReturns from './pages/PurchaseReturns';
import Purchase        from './pages/Purchase';

const queryClient = new QueryClient();

/** Where a user lands: the dashboard if they may see it, otherwise sales */
const HomeRedirect: React.FC = () => {
  const { hasPermission } = useAuth();
  return <Navigate to={hasPermission('dashboard.view') ? '/dashboard' : '/sales'} replace />;
};

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <Layout>{children}</Layout> : <Navigate to="/login" replace />;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <HomeRedirect /> : <>{children}</>;
};

const AppRoutes: React.FC = () => (
  <Routes>
    <Route path="/"          element={<HomeRedirect />} />
    <Route path="/login"     element={<PublicRoute><Login /></PublicRoute>} />
    <Route path="/register"  element={<PublicRoute><Register /></PublicRoute>} />
    <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
    <Route path="/service-tickets" element={<PrivateRoute><ServiceTickets /></PrivateRoute>} />
    <Route path="/quick-quote" element={<PrivateRoute><QuickQuote /></PrivateRoute>} />
    <Route path="/enquiries" element={<PrivateRoute><Enquiries /></PrivateRoute>} />
    <Route path="/follow-ups" element={<PrivateRoute><FollowUps /></PrivateRoute>} />
    <Route path="/expiry-tracking" element={<PrivateRoute><ExpiryTracking /></PrivateRoute>} />
    <Route path="/opening-stock-lines" element={<PrivateRoute><OpeningStockLines /></PrivateRoute>} />
    <Route path="/settings/payment-accounts" element={<PrivateRoute><PaymentAccounts /></PrivateRoute>} />
    <Route path="/accounts" element={<PrivateRoute><Accounts /></PrivateRoute>} />
    <Route path="/coa"       element={<PrivateRoute><ChartOfAccounts /></PrivateRoute>} />
    <Route path="/contacts"  element={<PrivateRoute><Contacts /></PrivateRoute>} />
    <Route path="/products"  element={<PrivateRoute><Products /></PrivateRoute>} />
    <Route path="/opening-balances" element={<PrivateRoute><OpeningBalances /></PrivateRoute>} />
    <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
    <Route path="/journals" element={<PrivateRoute><Journals /></PrivateRoute>} />
    <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
    <Route path="/inventory" element={<PrivateRoute><Inventory /></PrivateRoute>} />
    <Route path="/sales" element={<PrivateRoute><Sales /></PrivateRoute>} />
    <Route path="/stock-adjustment" element={<PrivateRoute><StockAdjustment /></PrivateRoute>} />
    <Route path="/stock-transfer" element={<PrivateRoute><StockTransfer /></PrivateRoute>} />
    <Route path="/physical-count" element={<PrivateRoute><PhysicalCount /></PrivateRoute>} />
    <Route path="/sales-returns" element={<PrivateRoute><SalesReturns /></PrivateRoute>} />
    <Route path="/purchase-returns" element={<PrivateRoute><PurchaseReturns /></PrivateRoute>} />
    <Route path="/purchase"  element={<PrivateRoute><Purchase /></PrivateRoute>} />
    <Route path="*"          element={<HomeRedirect />} />
  </Routes>
);

const App: React.FC = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <ToastProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </ToastProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;


















